/** @file previewPuppet: an instance over a private copy of the preview bundle with swappable clips. */
import type { Scene } from '@babylonjs/core/scene.js';
import type { Clip } from '@zilo/pose3d-format/types/clip';
import type { LoadedBundle } from '@zilo/pose3d-runtime-babylon/bundle/loadedBundle';
import { createPuppetInstance } from '@zilo/pose3d-runtime-babylon/instance/createPuppetInstance';
import type { PuppetInstance } from '@zilo/pose3d-runtime-babylon/instance/puppetInstanceTypes';

/**
 * `source` is the store's bundle (never mutated); `bundle` the instance's copy, whose own `clips` record `setClip`
 * swaps (the runtime looks the clip up by name on every `play`; onion ghosts share the copy).
 */
export type PreviewPuppet = {
  readonly source: LoadedBundle; readonly bundle: LoadedBundle; readonly instance: PuppetInstance; readonly names: string[];
  clip(name: string): Clip | undefined; setClip(name: string, clip: Clip): void; dispose(): void;
};

/** A shallow copy of `bundle` with its own `clips` record and an instance on it (joint meshes on, no blend). */
export async function previewPuppet(scene: Scene, bundle: LoadedBundle): Promise<PreviewPuppet> {
  const clips: Record<string, Clip> = { ...bundle.clips };
  const copy: LoadedBundle = { ...bundle, clips };
  const instance = await createPuppetInstance(scene, copy, { blendSeconds: 0 });
  return {
    source: bundle, bundle: copy, instance, names: Object.keys(bundle.puppet.nodes),
    clip: (name) => (Object.hasOwn(clips, name) ? clips[name] : undefined),
    setClip: (name, clip) => { clips[name] = clip; },
    dispose: () => { instance.dispose(); },
  };
}
